import { NAMESPACES, TERMS } from '../vocabulary/index.js';
import type { RawNode } from './builders.js';

/** mulberry32 — small, fast, and reproducible from a seed. */
export function createRandom(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const DPSE_TERMS = new Set(
  Object.values(TERMS)
    .filter((term) => term.iri.startsWith(NAMESPACES.dpem))
    .map((term) => term.term),
);

interface Writer {
  /** Randomly picks the alias or the `dpem:`-prefixed spelling of a term. */
  key(term: string): string;
  /** Randomly emits a many-valued term as a scalar when it has one value. */
  many(values: readonly string[]): unknown;
  /** Randomly emits a reference as `{"@id": …}` instead of a bare string. */
  ref(value: string): unknown;
  int(maxExclusive: number): number;
  bool(): boolean;
  shuffle<T>(items: readonly T[]): T[];
}

function writerFor(random: () => number): Writer {
  return {
    key(term) {
      return DPSE_TERMS.has(term) && random() < 0.5 ? `dpem:${term}` : term;
    },
    many(values) {
      return values.length === 1 && random() < 0.5 ? values[0] : [...values];
    },
    ref(value) {
      return random() < 0.5 ? value : { '@id': value };
    },
    int(maxExclusive) {
      return Math.floor(random() * maxExclusive);
    },
    bool() {
      return random() < 0.5;
    },
    shuffle(items) {
      const copy = [...items];
      for (let i = copy.length - 1; i > 0; i -= 1) {
        const j = Math.floor(random() * (i + 1));
        const a = copy[i];
        const b = copy[j];
        if (a !== undefined && b !== undefined) {
          copy[i] = b;
          copy[j] = a;
        }
      }
      return copy;
    },
  };
}

export interface GeneratedGraph {
  readonly document: Record<string, unknown>;
  readonly nodeCount: number;
}

/**
 * A random but well-formed DPSE document. It deliberately varies every degree of
 * freedom JSON-LD allows — term spelling (`phase` vs `dpem:phase`), single value
 * vs one-element array, bare id vs `{"@id": …}`, and node order — so a round-trip
 * property test exercises normalisation rather than one lucky shape.
 */
export function generateMethodDocument(seed: number): GeneratedGraph {
  const random = createRandom(seed);
  const w = writerFor(random);
  const nodes: RawNode[] = [];

  const phaseCount = 1 + w.int(3);
  const stageCount = 1 + w.int(5);
  const roleCount = 1 + w.int(4);
  const gateCount = 1 + w.int(3);
  const deliverableCount = 1 + w.int(3);
  const lensCount = 1 + w.int(3);
  const ceremonyCount = 1 + w.int(2);
  const practiceCount = w.int(3);
  const toolCount = w.int(3);

  const phases = Array.from({ length: phaseCount }, (_, i) => `phase:P${String(i)}`);
  const stages = Array.from({ length: stageCount }, (_, i) => `stage:S${String(i)}`);
  const roles = Array.from({ length: roleCount }, (_, i) => `role:R${String(i)}`);
  const gates = Array.from({ length: gateCount }, (_, i) => `gate:G${String(i)}`);
  const deliverables = Array.from({ length: deliverableCount }, (_, i) => `tmpl:T${String(i)}`);
  const lenses = Array.from({ length: lensCount }, (_, i) => `lens:L${String(i)}`);
  const ceremonies = Array.from({ length: ceremonyCount }, (_, i) => `comm:C${String(i)}`);
  const practices = Array.from({ length: practiceCount }, (_, i) => `wp:W${String(i)}`);
  const tools = Array.from({ length: toolCount }, (_, i) => `tool:X${String(i)}`);

  const pick = <T>(items: readonly T[]): T => {
    const value = items[w.int(items.length)];
    if (value === undefined) throw new Error('generator picked from an empty list');
    return value;
  };

  nodes.push({
    '@id': 'method:generated',
    '@type': 'dpem:Method',
    name: `Generated method ${String(seed)}`,
    description: 'Randomly generated for round-trip property testing.',
    [w.key('status')]: w.bool() ? 'DRAFT' : 'IN_REVIEW',
    [w.key('hasPhase')]: w.many(phases),
    [w.key('hasStage')]: w.many(stages),
    [w.key('hasRole')]: w.many(roles),
    [w.key('hasGate')]: w.many(gates),
    [w.key('hasDeliverable')]: w.many(deliverables),
    [w.key('hasLens')]: w.many(lenses),
    [w.key('hasCeremony')]: w.many(ceremonies),
    ...(practices.length > 0 ? { [w.key('hasWorkPractice')]: w.many(practices) } : {}),
    ...(tools.length > 0 ? { [w.key('hasTool')]: w.many(tools) } : {}),
  });

  phases.forEach((id, index) => {
    nodes.push({
      '@id': id,
      '@type': 'dpem:Phase',
      name: `Phase ${String(index)}`,
      [w.key('order')]: index,
    });
  });

  const activities: string[] = [];
  stages.forEach((id, index) => {
    const activityCount = 1 + w.int(4);
    const stageActivities = Array.from(
      { length: activityCount },
      (_, j) => `act:S${String(index)}.${String(j)}`,
    );
    activities.push(...stageActivities);
    nodes.push({
      '@id': id,
      '@type': 'dpem:Stage',
      name: `Stage ${String(index)}`,
      description: `Generated stage ${String(index)}.`,
      [w.key('order')]: index,
      [w.key('hasActivity')]: w.many(stageActivities),
      [w.key('inPhase')]: w.ref(pick(phases)),
      [w.key('phase')]: `Phase ${String(index % phases.length)}`,
      [w.key('workStream')]: w.many(['Generated stream']),
      [w.key('governedByGate')]: w.ref(pick(gates)),
      ...(w.bool() ? { [w.key('isMetaStage')]: false } : {}),
    });

    stageActivities.forEach((activityId, j) => {
      const next = stageActivities[j + 1];
      nodes.push({
        '@id': activityId,
        '@type': 'dpem:Activity',
        name: `Activity ${String(index)}.${String(j)}`,
        description: `Generated activity ${String(index)}.${String(j)}.`,
        [w.key('order')]: j,
        [w.key('performedBy')]: w.many([pick(roles)]),
        [w.key('produces')]: w.many([pick(deliverables)]),
        [w.key('usesLens')]: w.many([pick(lenses)]),
        [w.key('communication')]: w.many([pick(ceremonies)]),
        [w.key('governedByGate')]: w.ref(pick(gates)),
        [w.key('workPractice')]: 'Generated practice',
        ...(next === undefined ? {} : { [w.key('precedes')]: w.many([next]) }),
        ...(w.bool() ? { [w.key('automation')]: 'assisted' } : {}),
        ...(w.bool() ? { [w.key('writeup')]: `## Activity ${String(j)}\n\nGenerated body.` } : {}),
      });
    });
  });

  practices.forEach((id, index) => {
    nodes.push({
      '@id': id,
      '@type': 'dpem:WorkPractice',
      name: `Practice ${String(index)}`,
      [w.key('order')]: index,
      ...(index > 0 ? { [w.key('parentPractice')]: w.ref(`wp:W0`) } : {}),
    });
  });
  if (practices.length > 1) {
    const root = nodes.find((node) => node['@id'] === 'wp:W0');
    if (root !== undefined) root[w.key('hasWorkPractice')] = w.many(practices.slice(1));
  }

  roles.forEach((id, index) => {
    nodes.push({
      '@id': id,
      '@type': 'dpem:Role',
      name: `Role ${String(index)}`,
      [w.key('responsibilities')]: `Responsibilities of role ${String(index)}.`,
      [w.key('isAgentRole')]: w.bool(),
    });
  });

  gates.forEach((id, index) => {
    nodes.push({
      '@id': id,
      '@type': w.bool() ? 'dpem:Gate' : 'dpem:GateAgentic',
      name: `Gate ${String(index)}`,
      [w.key('exitCriteria')]: `Exit criteria ${String(index)}.`,
      [w.key('performedBy')]: w.many([pick(roles)]),
      ...(w.bool() ? { [w.key('loopsTo')]: w.many([pick(stages)]) } : {}),
    });
  });

  deliverables.forEach((id, index) => {
    const versionId = `tv:V${String(index)}`;
    nodes.push({
      '@id': id,
      '@type': w.bool() ? 'dpem:Template' : 'dpem:Deliverable',
      name: `Deliverable ${String(index)}`,
      [w.key('templateFile')]: `templates/generated-${String(index)}.md`,
      [w.key('mandatory')]: w.bool(),
      [w.key('hasTemplateVersion')]: w.many([versionId]),
      [w.key('currentTemplateVersion')]: w.ref(versionId),
    });
    nodes.push({
      '@id': versionId,
      '@type': 'dpem:TemplateVersion',
      [w.key('version')]: 1,
      [w.key('body')]: `# Deliverable ${String(index)}\n\nGenerated template body.`,
      [w.key('templateOf')]: w.ref(id),
    });
  });

  lenses.forEach((id, index) => {
    nodes.push({
      '@id': id,
      '@type': w.bool() ? 'dpem:ArchitectureLens' : 'dpem:Lens',
      name: `Lens ${String(index)}`,
      [w.key('workStreamMapping')]: `Stream ${String(index)}`,
    });
  });

  ceremonies.forEach((id, index) => {
    nodes.push({
      '@id': id,
      '@type': w.bool() ? 'dpem:Communication' : 'dpem:Ceremony',
      name: `Ceremony ${String(index)}`,
      [w.key('when')]: `S${String(index)}`,
    });
  });

  tools.forEach((id, index) => {
    nodes.push({
      '@id': id,
      '@type': 'dpem:Tool',
      name: `Tool ${String(index)}`,
      [w.key('category')]: 'generated',
      [w.key('links')]: w.many(['https://example.org/tool']),
    });
  });

  if (activities.length > 0 && w.bool()) {
    nodes.push({
      '@id': 'tdec:D0',
      '@type': 'dpem:TailoringDecision',
      [w.key('decision')]: 'INCLUDE',
      [w.key('appliesTo')]: w.ref(pick(activities)),
      [w.key('rationale')]: 'Kept as is.',
    });
  }

  return {
    document: { '@context': { ...requiredContext() }, '@graph': w.shuffle(nodes) },
    nodeCount: nodes.length,
  };
}

/**
 * The generator writes the SPEC skeleton context rather than the full canonical
 * one, so parse is exercised against a legitimately smaller context.
 */
function requiredContext(): Record<string, unknown> {
  return {
    dpem: NAMESPACES.dpem,
    schema: NAMESPACES.schema,
    rdfs: NAMESPACES.rdfs,
    name: 'rdfs:label',
    description: 'schema:description',
    hasStage: { '@id': 'dpem:hasStage', '@type': '@id' },
    hasActivity: { '@id': 'dpem:hasActivity', '@type': '@id' },
    performedBy: { '@id': 'dpem:performedBy', '@type': '@id' },
    produces: { '@id': 'dpem:produces', '@type': '@id' },
    usesLens: { '@id': 'dpem:usesLens', '@type': '@id' },
    governedByGate: { '@id': 'dpem:governedByGate', '@type': '@id' },
    communication: { '@id': 'dpem:communication', '@type': '@id' },
    precedes: { '@id': 'dpem:precedes', '@type': '@id' },
    loopsTo: { '@id': 'dpem:loopsTo', '@type': '@id' },
    order: 'dpem:order',
  };
}
